import chatActions from "actions/chat";
import notificationActions from "actions/notification";
import { chatTypesEnum } from "helpers/enums";
import { connect, generatedActions } from "helpers/redux";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { chatsCount, read } from "sources/books/chats";
import { getUnreadPlatformNewsCount } from "sources/books/platformNews";
import { getChatCountWithUnreadMessages } from "sources/carChats";

const UnreadItemsContext = createContext();

export const useUnreadItems = () => useContext(UnreadItemsContext);

const UnreadItemsProvider = ({ children, reduxActions }) => {
    const debounceTimeout = useRef(null);
    const newsTimer = useRef(null);

    const location = useLocation();
    const pathnameRef = useRef(location.pathname);
    const [unreadItems, setUnreadItems] = useState({
        notifications: 0,
        chats: 0,
        "car-chats": 0,
        news: {},
        "fix-route-chats": 0,
    });

    const getNewsCounter = () =>
        getUnreadPlatformNewsCount().then((data) => setUnreadItems((items) => ({ ...items, news: data })));

    useEffect(() => {
        pathnameRef.current = location.pathname;
    }, [location.pathname]);

    useEffect(() => {
        chatsCount().then((data) =>
            setUnreadItems((items) => ({
                ...items,
                chats: data.chatsCount,
                notifications: data.notificationsCount,
                "fix-route-chats": data.fixRouteChatsCount,
            })),
        );
        getNewsCounter();
        getChatCountWithUnreadMessages().then((data) =>
            setUnreadItems((items) => ({ ...items, "car-chats": data.count })),
        );

        newsTimer.current = setInterval(getNewsCounter, 10 * 60 * 1000); // every 10 minutes

        return () => {
            clearTimeout(debounceTimeout.current);
            clearInterval(newsTimer.current);
        };
    }, []);

    const setUnreadChatsCount = useCallback(
        (data) => {
            data.chats.forEach((item) => {
                switch (item.type) {
                    case chatTypesEnum.BidChat: {
                        // 5 seconds debounce
                        clearTimeout(debounceTimeout.current);
                        debounceTimeout.current = setTimeout(() => {
                            setUnreadItems((items) => ({ ...items, notifications: item.unreadCount }));
                        }, 5000);
                        break;
                    }
                    case chatTypesEnum.DriverChat: {
                        if (pathnameRef.current.includes("chats")) {
                            reduxActions.chat.merge();
                        }
                        setUnreadItems((items) => ({ ...items, chats: item.unreadCount }));
                        break;
                    }
                    case chatTypesEnum.FixRouteBidChat: {
                        setUnreadItems((items) => ({ ...items, "fix-route-chats": item.unreadCount }));
                        break;
                    }
                }
            });
        },
        [reduxActions.chat, setUnreadItems],
    );

    const readAllItems = useCallback((params) => {
        return read(params).then(() => {
            if (params.chatType === chatTypesEnum.BidChat) {
                reduxActions.notification.readAll();
                return setUnreadItems((items) => ({ ...items, notifications: 0 }));
            } else {
                reduxActions.chat.readAll();
                return setUnreadItems((items) => ({ ...items, chats: 0 }));
            }
        });
    }, [reduxActions.chat, reduxActions.notification, setUnreadItems]);

    const readItems = useCallback((params) => {
        const { chatId, chatType } = params;
        if (chatType === chatTypesEnum.BidChat) {
            reduxActions.notification.readOne(params);
            setUnreadItems((items) => ({
                ...items,
                notifications: items.notifications - 1 < 0 ? 0 : items.notifications - 1,
            }));
        } else if (chatType === chatTypesEnum.FixRouteBidChat) {
            setUnreadItems((items) => ({
                ...items,
                "fix-route-chats": items["fix-route-chats"] - 1 < 0 ? 0 : items["fix-route-chats"] - 1,
            }));
        } else {
            reduxActions.chat.readOne(params);
            setUnreadItems((items) => ({ ...items, chats: items.chats - 1 < 0 ? 0 : items.chats - 1 }));
        }
        return read(chatId ? params : undefined);
    }, [reduxActions.chat, reduxActions.notification, setUnreadItems]);

    const value = useMemo(
        () => ({ unreadItems, setUnreadItems, setUnreadChatsCount, readAllItems, readItems }),
        [unreadItems, setUnreadItems, setUnreadChatsCount, readAllItems, readItems],
    );

    return <UnreadItemsContext.Provider value={value}>{children}</UnreadItemsContext.Provider>;
};

const mapDispatch = (dispatch) => ({
    reduxActions: {
        notification: {
            readAll: () => dispatch(notificationActions.readAllNotifications()),
            readOne: (params) => dispatch(notificationActions.readNotification(params)),
        },
        chat: {
            readAll: () => dispatch(chatActions.readAllChats()),
            readOne: (params) => dispatch(chatActions.readChat(params)),
            merge: () => dispatch(generatedActions.chats.merge({})),
        },
    },
});

const ConnectedUnreadItemsProvider = connect(() => ({}), mapDispatch)(UnreadItemsProvider);

export { ConnectedUnreadItemsProvider as UnreadItemsProvider };
